import type { Row } from '@tanstack/react-table'
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual'

import { cn } from '@/utils'
import { DataTableCell } from './DataTableCell'
import type { TableData } from './types'

// Floor height for the cells row, matching the virtualizer's estimateSize. The row grows past this
// to fit its content; an expanded row's extra body sits below and never stretches the cells.
const CELLS_ROW_MIN_HEIGHT = 40

interface DataTableRowProps {
  rows: Row<TableData>[]
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>
  virtualRow: VirtualItem
  onRowClick?: (row: Row<TableData>) => void
  renderSubComponent?: (row: Row<TableData>) => React.ReactNode
  rowAction?: (row: Row<TableData>) => React.ReactNode
}

export const DataTableRow = ({
  rows,
  virtualRow,
  rowVirtualizer,
  onRowClick,
  renderSubComponent,
  rowAction,
}: DataTableRowProps) => {
  const row = rows[virtualRow.index]
  const isSelected = row.getIsSelected()
  const isExpandable = !!renderSubComponent && row.getCanExpand()
  const isExpanded = row.getIsExpanded()
  const isClickable = !!onRowClick || isExpandable

  const handleRowClick = () => {
    onRowClick?.(row)
    if (isExpandable) row.toggleExpanded()
  }

  return (
    <div
      data-index={virtualRow.index}
      ref={rowVirtualizer.measureElement}
      className={cn(
        'absolute left-0 top-0 w-(--table-width)',
        virtualRow.index !== rows.length - 1 && 'border-b border-border/80',
      )}
      style={{ transform: `translateY(${virtualRow.start}px)` }}
    >
      <div
        className={cn(
          'flex items-center px-4',
          isClickable && 'cursor-pointer',
          // An opaque background in every state, so a floating action can borrow it and match the
          // row it sits on, hover and all.
          isSelected ? 'bg-blue-50' : 'bg-white hover:bg-blue-1',
        )}
        style={{ minHeight: `${CELLS_ROW_MIN_HEIGHT}px` }}
        onClick={isClickable ? handleRowClick : undefined}
      >
        {row.getVisibleCells().map((cell) => (
          <DataTableCell key={cell.id} cell={cell} />
        ))}

        {rowAction && (
          // The row's own background behind the action, painted all the way to the right edge, so
          // the cells it floats over never read through it or peek out beside it.
          <div className="sticky right-0 z-10 ml-auto flex shrink-0 items-center bg-inherit pl-3 pr-4">
            {rowAction(row)}
          </div>
        )}
      </div>
      {isExpanded && isExpandable && renderSubComponent?.(row)}
    </div>
  )
}
